import React, { useEffect, useRef, useState } from "react";
import {
  Brain,
  GraduationCap,
  CheckCircle2,
  Menu,
  X,
  Zap,
  MessageCircle,
  Calculator,
  BadgePercent,
  Truck,
  Headset,
  UserPlus,
  KeyRound,
  Banknote,
  Layers,
  LineChart,
  Monitor,
  PencilLine,
  Sparkles,
  ShieldCheck,
  WifiOff,
  Minus,
  Plus,
} from "lucide-react";
import { Cloud, StarBurst, SparkleDoodle, Blob } from "./graphics";

/* ------------------------------------------------------------------ */
/*  Tokens (same as Homepage)                                           */
/* ------------------------------------------------------------------ */

const INK = "#181611";
const CREAM = "#FDF8F0";
const CREAM_CARD = "#FFF4DE";
const MINT = "#DCEEE3";
const MINT_DEEP = "#BFDFCE";
const PEACH_1 = "#F8CE85";
const PEACH_2 = "#F4AE59";
const ORANGE = "#EE7C3B";
const ORANGE_DEEP = "#D9642A";
const INDIGO = "#3D4EAD";
const INDIGO_DEEP = "#2E3B8A";
const FOREST = "#2F6B4F";
const MUTED = "#5B5748";
const LINE = "#EADFC4";

const WHATSAPP_NUMBER = "2347044257292";
const waLink = (msg) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

/* ------------------------------------------------------------------ */
/*  Pricing — edit here                                                 */
/*  Unit price ₦2,000 (same for everyone) · reseller bulk discounts      */
/*  25–50% off · cheapest ₦1,000/code at 50+ codes                       */
/* ------------------------------------------------------------------ */

const RETAIL_PRICE = 2000;

const PRODUCTS = [
  {
    id: "jamb",
    name: "JAMB Desktop CBT",
    icon: Brain,
    blurb: "Windows + Mac \u00b7 offline \u00b7 2000\u20132026 questions",
    retail: RETAIL_PRICE,
  },
  {
    id: "waec",
    name: "WAEC Desktop CBT",
    icon: GraduationCap,
    blurb: "Windows + Mac \u00b7 Objective + Theory, offline",
    retail: RETAIL_PRICE,
  },
];

const TIERS = [
  { min: 1, max: 4, price: 1500, tag: "Starter · 25% off" },
  { min: 5, max: 19, price: 1300, tag: "Hustle · 35% off" },
  { min: 20, max: 49, price: 1100, tag: "Pro · 45% off" },
  { min: 50, max: Infinity, price: 1000, tag: "Boss · 50% off" },
];

function tierFor(qty) {
  return TIERS.find((t) => qty >= t.min && qty <= t.max) || TIERS[0];
}

/* ------------------------------------------------------------------ */
/*  Small utilities (mirrors Homepage)                                  */
/* ------------------------------------------------------------------ */

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0 }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: `opacity 0.6s cubic-bezier(.2,.7,.3,1) ${delay}ms, transform 0.6s cubic-bezier(.2,.7,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function HighlightWord({ children, bg = ORANGE, color = "#FFFFFF", rotate = "-1.2deg" }) {
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color,
        padding: "2px 18px 8px",
        borderRadius: "40% 45% 42% 48% / 55% 48% 52% 45%",
        transform: `rotate(${rotate})`,
        position: "relative",
      }}
    >
      {children}
    </span>
  );
}

function Pill({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "12px",
        fontWeight: 600,
        color: MUTED,
        background: CREAM_CARD,
        border: `1.5px solid ${LINE}`,
        borderRadius: "999px",
        padding: "6px 16px",
      }}
    >
      {children}
    </span>
  );
}

const naira = (n) => `₦${n.toLocaleString()}`;

/* ------------------------------------------------------------------ */
/*  Calculator                                                          */
/* ------------------------------------------------------------------ */

const clampQty = (v) => {
  if (Number.isNaN(v)) return 1;
  return Math.min(500, Math.max(1, Math.floor(v)));
};

function PriceCalculator() {
  const [productId, setProductId] = useState("jamb");
  const [qty, setQty] = useState(10);

  const product = PRODUCTS.find((p) => p.id === productId);
  const tier = tierFor(qty);
  const baseUnit = RETAIL_PRICE;
  const unitCost = tier.price;
  const discountPerUnit = baseUnit - unitCost;
  const discountPct = Math.round((discountPerUnit / baseUnit) * 100);
  const totalCost = unitCost * qty;
  const profit = (baseUnit - unitCost) * qty;

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: "24px",
        overflow: "hidden",
        boxShadow: "0 18px 40px rgba(24,22,17,0.12)",
      }}
    >
      <div
        style={{
          background: INK,
          padding: "22px 28px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            background: ORANGE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Calculator size={20} color="#FFFFFF" strokeWidth={2} />
        </div>
        <div>
          <div
            style={{
              fontFamily: "'Baloo 2', sans-serif",
              fontWeight: 700,
              fontSize: "18px",
              color: CREAM,
              lineHeight: 1.2,
            }}
          >
            Reseller Price Calculator
          </div>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "12.5px",
              color: "rgba(253,248,240,0.7)",
            }}
          >
            Buy more, pay less — bulk discounts applied automatically
          </div>
        </div>
      </div>

      <div style={{ padding: "28px" }}>
        <label
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            color: INK,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "block",
            marginBottom: "10px",
          }}
        >
          Select product category
        </label>
        <div style={{ display: "flex", gap: "10px", marginBottom: "22px", flexWrap: "wrap" }}>
          {PRODUCTS.map((p) => {
            const Icon = p.icon;
            const active = p.id === productId;
            return (
              <button
                key={p.id}
                onClick={() => setProductId(p.id)}
                style={{
                  flex: "1 1 0",
                  minWidth: "180px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 16px",
                  borderRadius: "14px",
                  border: active ? `2px solid ${FOREST}` : `1.5px solid ${LINE}`,
                  background: active ? MINT : "#FAF8F2",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Icon size={22} color={active ? FOREST : MUTED} strokeWidth={1.8} />
                <span>
                  <span
                    style={{
                      display: "block",
                      fontFamily: "'Baloo 2', sans-serif",
                      fontWeight: 700,
                      fontSize: "14.5px",
                      color: INK,
                    }}
                  >
                    {p.name}
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "11.5px",
                      color: MUTED,
                    }}
                  >
                    Retail {naira(p.retail)} · Reseller from {naira(1000)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <label
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            color: INK,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            display: "block",
            marginBottom: "10px",
          }}
        >
          Quantity (activation codes)
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
          <button
            onClick={() => setQty((q) => clampQty(q - 1))}
            aria-label="Decrease quantity"
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              border: `1.5px solid ${LINE}`,
              background: CREAM_CARD,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Minus size={18} color={INK} />
          </button>
          <input
            type="number"
            min={1}
            max={500}
            value={qty}
            onChange={(e) => {
              if (e.target.value === "") return;
              setQty(clampQty(parseInt(e.target.value, 10)));
            }}
            style={{
              flex: 1,
              padding: "12px 16px",
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: "'Space Grotesk', sans-serif",
              borderRadius: "12px",
              border: `1.5px solid ${LINE}`,
              outline: "none",
              textAlign: "center",
              color: INK,
            }}
          />
          <button
            onClick={() => setQty((q) => clampQty(q + 1))}
            aria-label="Increase quantity"
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              border: "none",
              background: INK,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Plus size={18} color={CREAM} />
          </button>
        </div>

        <input
          type="range"
          min={1}
          max={100}
          value={Math.min(qty, 100)}
          onChange={(e) => setQty(clampQty(parseInt(e.target.value, 10)))}
          style={{ width: "100%", accentColor: FOREST, marginBottom: "6px" }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "11px",
            color: MUTED,
            marginBottom: "20px",
          }}
        >
          <span>1</span>
          <span
            style={{
              background: MINT,
              color: FOREST,
              fontWeight: 700,
              padding: "2px 10px",
              borderRadius: "999px",
            }}
          >
            {tier.tag} · {naira(unitCost)}/code
          </span>
          <span>100+</span>
        </div>

        <div
          style={{
            border: `1.5px solid ${LINE}`,
            borderRadius: "16px",
            overflow: "hidden",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              padding: "16px 18px",
              background: "#FFFFFF",
              borderBottom: `1.5px solid ${LINE}`,
            }}
          >
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: MUTED,
              }}
            >
              Unit price
            </span>
            <span
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                fontSize: "20px",
                color: INK,
              }}
            >
              {naira(baseUnit)}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              padding: "16px 18px",
              background: "#EAF6EF",
              borderBottom: `1.5px solid ${LINE}`,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "13.5px",
                fontWeight: 600,
                color: FOREST,
              }}
            >
              <BadgePercent size={16} />
              Bulk discount
            </span>
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: "15px",
                color: FOREST,
                textAlign: "right",
              }}
            >
              {discountPerUnit > 0
                ? `−${discountPct}% (−${naira(discountPerUnit)}/unit)`
                : "No discount · buy 5+ to unlock"}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              padding: "16px 18px",
              background: "#FFFFFF",
              borderBottom: `1.5px solid ${LINE}`,
            }}
          >
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: MUTED,
              }}
            >
              Effective unit price
            </span>
            <span
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                fontSize: "20px",
                color: FOREST,
              }}
            >
              {naira(unitCost)}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              padding: "18px",
              background: "#EFF3F1",
            }}
          >
            <span>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: MUTED,
                }}
              >
                Total · {qty} code{qty > 1 ? "s" : ""}
              </span>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "11.5px",
                  color: MUTED,
                }}
              >
                what you pay us on WhatsApp
              </span>
            </span>
            <span
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                fontSize: "24px",
                color: INK,
              }}
            >
              {naira(totalCost)}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              padding: "18px",
              background: FOREST,
            }}
          >
            <span>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                Your profit if all sold
              </span>
              <span
                style={{
                  display: "block",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "11.5px",
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                sold at {naira(baseUnit)} each
              </span>
            </span>
            <span
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 700,
                fontSize: "24px",
                color: "#FFFFFF",
              }}
            >
              {naira(profit)}
            </span>
          </div>
        </div>

        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "11.5px",
            color: MUTED,
            textAlign: "center",
            margin: "0 0 12px",
            lineHeight: 1.6,
          }}
        >
          Discount tiers:{" "}
          {TIERS.map(
            (t) =>
              `${t.min}–${t.max === Infinity ? "+" : t.max} units: ${Math.round(((baseUnit - t.price) / baseUnit) * 100)}% off`
          ).join(" · ")}
        </p>

        <a
          href={waLink(
            `Hello Kinteron, I want reseller codes: ${qty} x ${product.name} (${naira(unitCost)} each = ${naira(totalCost)}). My name is ___`
          )}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            background: "#25D366",
            color: "#FFFFFF",
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "15px",
            padding: "15px",
            borderRadius: "12px",
            textDecoration: "none",
            marginTop: "6px",
          }}
        >
          <MessageCircle size={18} />
          Order {qty} code{qty > 1 ? "s" : ""} on WhatsApp
        </a>
        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "12px",
            color: MUTED,
            textAlign: "center",
            marginTop: "12px",
          }}
        >
          Codes delivered as KINT-XXXX-XXXX-XXXX · pay on confirmation · profit
          assumes you sell at {naira(product.retail)} each.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Reseller() {
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const calcRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToCalc = () =>
    calcRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { overflow-x: hidden; }
        body { background: ${CREAM}; overflow-x: hidden; max-width: 100vw; }
        .cta-solid {
          display: inline-flex; align-items: center; gap: 9px;
          background: ${ORANGE}; color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px;
          padding: 15px 28px; border-radius: 999px; border: none; cursor: pointer;
          text-decoration: none; box-shadow: 0 5px 0 ${ORANGE_DEEP};
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cta-solid:hover { transform: translateY(2px); box-shadow: 0 3px 0 ${ORANGE_DEEP}; }
        .cta-whatsapp {
          display: inline-flex; align-items: center; gap: 9px;
          background: #25D366; color: #FFFFFF;
          font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 15px;
          padding: 15px 28px; border-radius: 999px; border: none; cursor: pointer;
          text-decoration: none; box-shadow: 0 5px 0 #1DA851;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cta-whatsapp:hover { transform: translateY(2px); box-shadow: 0 3px 0 #1DA851; }
        .cta-line {
          display: inline-flex; align-items: center; gap: 9px;
          background: #FFFFFF; color: ${INK};
          font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 14.5px;
          padding: 13px 22px; border-radius: 999px; border: 1.5px solid ${LINE};
          cursor: pointer; text-decoration: none;
        }
        .cta-line:hover { background: ${CREAM_CARD}; }
        .nav-tab {
          font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 14.5px;
          color: ${INK}; background: transparent; border: none; cursor: pointer;
          padding: 8px 6px; text-decoration: none;
        }
        .nav-tab.active { color: ${ORANGE_DEEP}; border-bottom: 2.5px solid ${ORANGE}; }
        .reseller-card { transition: transform 0.25s ease, box-shadow 0.25s ease; box-shadow: 0 1px 2px rgba(24,22,17,0.05); }
        .reseller-card:hover { transform: translateY(-4px); box-shadow: 0 14px 28px rgba(24,22,17,0.1); }
        @media (max-width: 940px) {
          .nav-links { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .hero-grid, .why-grid, .steps-grid, .tiers-grid, .faq-grid { grid-template-columns: 1fr !important; }
          .calc-grid { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) { .calc-grid { grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ background: CREAM, minHeight: "100vh", color: INK, overflowX: "hidden" }}>
        {/* NAVBAR */}
        <nav
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px 0 40px",
            height: "76px",
            background: navScrolled || mobileOpen ? "rgba(253,248,240,0.96)" : "transparent",
            backdropFilter: navScrolled || mobileOpen ? "blur(10px)" : "none",
            borderBottom: navScrolled || mobileOpen ? `1px solid ${LINE}` : "1px solid transparent",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "36px" }}>
            <a
              href="/"
              style={{
                fontFamily: "'Baloo 2', sans-serif",
                fontWeight: 800,
                fontSize: "23px",
                color: INK,
                textDecoration: "none",
              }}
            >
              Kinteron
            </a>
            <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: "26px" }}>
              <a href="/#apps" className="nav-tab">Apps</a>
              <a href="/desktop" className="nav-tab">Desktop</a>
              <a href="/desktop#download" className="nav-tab">Download</a>
              <a href="/reseller" className="nav-tab active">Reseller</a>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <a
              href={waLink("Hello Kinteron, I'd like to become a reseller. My name is ___")}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-whatsapp nav-cta-desktop"
              style={{ padding: "11px 20px", fontSize: "13.5px" }}
            >
              <MessageCircle size={15} />
              Become a Reseller
            </a>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              style={{
                display: "none",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                border: "none",
                background: mobileOpen ? INK : CREAM_CARD,
                color: mobileOpen ? CREAM : INK,
                cursor: "pointer",
              }}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {mobileOpen && (
          <div
            style={{
              position: "sticky",
              top: "76px",
              zIndex: 99,
              background: "#FFFFFF",
              borderBottom: `1px solid ${LINE}`,
              padding: "8px 20px 20px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            {[
              { label: "Apps", href: "/#apps" },
              { label: "Desktop CBT programs", href: "/desktop" },
              { label: "Download the apps", href: "/desktop#download" },
              { label: "Reseller (you are here)", href: "/reseller" },
            ].map((l) => (
              <a key={l.label} href={l.href} className="nav-tab" style={{ padding: "14px 8px" }}>
                {l.label}
              </a>
            ))}
            <a
              href={waLink("Hello Kinteron, I'd like to become a reseller. My name is ___")}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-whatsapp"
              style={{ justifyContent: "center", marginTop: "8px" }}
            >
              <MessageCircle size={15} />
              Become a Reseller
            </a>
          </div>
        )}

        {/* HERO */}
        <section
          style={{
            background: `linear-gradient(180deg, ${PEACH_1} 0%, ${PEACH_2} 100%)`,
            padding: "64px 40px 0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Cloud width={150} style={{ top: "30px", left: "4%" }} />
          <Cloud width={90} opacity={0.6} style={{ top: "120px", right: "8%" }} />
          <StarBurst size={26} color="#FFFFFF" className="twinkle-slow" style={{ top: "60px", left: "22%" }} />
          <SparkleDoodle size={22} color="#D9642A" className="twinkle-slow" style={{ top: "90px", right: "24%" }} />

          <div
            className="hero-grid"
            style={{
              maxWidth: "1180px",
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: "1.05fr 0.95fr",
              gap: "40px",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div style={{ paddingBottom: "70px" }}>
              <Pill>
                <Sparkles size={13} style={{ marginRight: "6px" }} />
                Resellers wanted · JAMB + WAEC Desktop
              </Pill>
              <h1
                style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 700,
                  fontSize: "clamp(38px, 5vw, 58px)",
                  lineHeight: 1.08,
                  margin: "22px 0 8px",
                  color: INK,
                }}
              >
                Become a Kinteron
                <br />
                <HighlightWord bg={INK} color={CREAM}>Reseller</HighlightWord>
              </h1>
              <p
                style={{
                  fontFamily: "'Baloo 2', sans-serif",
                  fontWeight: 600,
                  fontSize: "clamp(18px, 2.4vw, 24px)",
                  color: ORANGE_DEEP,
                  marginTop: "14px",
                }}
              >
                Buy at ₦1,000 · sell at ₦2,000 · keep up to 50% profit
              </p>
              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "16px",
                  color: "#3B3728",
                  lineHeight: 1.65,
                  margin: "16px 0 30px",
                  maxWidth: "460px",
                }}
              >
                Join tutorial centres, cybercafés and campus hustlers already earning
                selling offline JAMB &amp; WAEC Desktop CBT — activation codes delivered
                on WhatsApp, usable without internet.
              </p>
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                <a
                  href={waLink("Hello Kinteron, I'd like to become a reseller. My name is ___")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-whatsapp"
                >
                  <MessageCircle size={16} />
                  Join as Reseller
                </a>
                <button className="cta-line" onClick={scrollToCalc}>
                  <Calculator size={16} />
                  Try price calculator
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "22px",
                  marginTop: "28px",
                  flexWrap: "wrap",
                }}
              >
                {[
                  { n: "₦1,000", l: "per code in bulk (50+)" },
                  { n: "−50%", l: "bulk discount at 50+" },
                  { n: "KINT-…", l: "code per machine" },
                ].map((s) => (
                  <div key={s.l}>
                    <div style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "22px", color: INK }}>
                      {s.n}
                    </div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: "#3B3728" }}>
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero side card — example order */}
            <div style={{ display: "flex", justifyContent: "center", paddingBottom: "70px" }}>
              <div
                style={{
                  width: "min(360px, 100%)",
                  background: "#FFFFFF",
                  borderRadius: "22px",
                  padding: "24px",
                  boxShadow: "0 24px 48px rgba(24,22,17,0.16)",
                  transform: "rotate(-1.5deg)",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "14px",
                    paddingBottom: "12px",
                    borderBottom: `1.5px dashed ${LINE}`,
                  }}
                >
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: FOREST, fontWeight: 600 }}>
                    EXAMPLE ORDER · 10 CODES
                  </span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: MUTED }}>
                    JAMB DESKTOP
                  </span>
                </div>
                {[
                  { k: "Unit price", v: "₦2,000", sub: "same for everyone" },
                  { k: "Bulk discount", v: "−₦700/unit", sub: "−35% · Hustle tier (10 codes)" },
                  { k: "You pay", v: "₦13,000", sub: "10 × ₦1,300 effective" },
                  { k: "Profit if all sold", v: "₦7,000", sub: "sold at ₦2,000 each", green: true },
                ].map((r) => (
                  <div
                    key={r.k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 14px",
                      borderRadius: "12px",
                      marginBottom: "8px",
                      background: r.green ? MINT : "#FAF8F2",
                      border: r.green ? `1.5px solid ${FOREST}` : `1.5px solid ${LINE}`,
                    }}
                  >
                    <span>
                      <span style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "12px", color: MUTED, fontWeight: 600 }}>
                        {r.k}
                      </span>
                      <span style={{ display: "block", fontFamily: "'Space Grotesk', sans-serif", fontSize: "11px", color: MUTED }}>
                        {r.sub}
                      </span>
                    </span>
                    <span style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "20px", color: r.green ? FOREST : INK }}>
                      {r.v}
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: "12px", padding: "10px 12px", borderRadius: "10px", background: CREAM_CARD, display: "flex", gap: "8px", alignItems: "center" }}>
                  <KeyRound size={15} color={ORANGE_DEEP} />
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "11.5px", color: "#3B3728" }}>
                    Codes arrive on WhatsApp — e.g. KINT-7X2Q-9M4A-31ZD
                  </span>
                </div>
              </div>
            </div>
          </div>

          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: "100%", height: "60px", display: "block", marginTop: "10px" }}>
            <path d="M0,40 C240,90 480,0 720,20 C960,40 1200,90 1440,30 L1440,80 L0,80 Z" fill={MINT} />
          </svg>
        </section>

        {/* CALCULATOR */}
        <section ref={calcRef} style={{ background: MINT, padding: "20px 40px 90px" }}>
          <div style={{ maxWidth: "760px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "32px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#33512F", letterSpacing: "0.06em" }}>
                  BUY MORE, PAY LESS
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.6vw, 42px)", color: INK, marginTop: "12px" }}>
                  See exactly what you'll <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">earn</HighlightWord>
                </h2>
              </div>
            </Reveal>
            <Reveal delay={90}>
              <PriceCalculator />
            </Reveal>

            {/* Tier table */}
            <Reveal delay={140}>
              <div
                className="tiers-grid"
                style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginTop: "20px" }}
              >
                {TIERS.map((t) => (
                  <div key={t.min} style={{ background: "#FFFFFF", borderRadius: "14px", padding: "16px 14px", textAlign: "center", border: `1.5px solid ${LINE}` }}>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: MUTED, marginBottom: "4px" }}>
                      {t.max === Infinity ? `${t.min}+ codes` : `${t.min}–${t.max} codes`}
                    </div>
                    <div style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "20px", color: INK }}>
                      {naira(t.price)}
                    </div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "11px", color: FOREST, fontWeight: 600 }}>
                      /code · {Math.round(((RETAIL_PRICE - t.price) / RETAIL_PRICE) * 100)}% off
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* INSIDE EVERY COPY — the CBT features resellers are selling */}
        <section style={{ background: CREAM, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: ORANGE_DEEP, letterSpacing: "0.06em" }}>
                  WHAT YOU'RE SELLING
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 44px)", color: INK, marginTop: "12px" }}>
                  Inside every <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">copy</HighlightWord>
                </h2>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: MUTED, maxWidth: "520px", margin: "16px auto 0", lineHeight: 1.7 }}>
                  JAMB &amp; WAEC Desktop CBT — demo any of these on a customer's
                  computer and the software sells itself.
                </p>
              </div>
            </Reveal>
            <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "22px" }}>
              {[
                { icon: WifiOff, title: "Works fully offline", body: "Install once and run forever without internet — the #1 reason homes, villages and centres buy it.", tone: FOREST },
                { icon: Brain, title: "AI tutor on every question", body: "Answers aren't just marked — the built-in AI verifies each one and teaches the reasoning behind it.", tone: ORANGE },
                { icon: PencilLine, title: "Topic-by-topic practice", body: "Drill weak topics one by one, then sit full timed simulations that mirror the real exam.", tone: INDIGO },
                { icon: Monitor, title: "Real CBT experience", body: "Official JAMB calculator, exam keys and timer, with 90,000+ past questions from 2000–2026.", tone: ORANGE_DEEP },
                { icon: LineChart, title: "History & analytics", body: "Every attempt is saved with performance analysis — students see progress, parents see proof.", tone: INDIGO_DEEP },
                { icon: Layers, title: "Flashcards for quick revision", body: "Our newest addition — bite-size flashcards sourced from past questions for fast daily revision.", tone: FOREST },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.title} delay={i * 70}>
                    <div className="reseller-card" style={{ background: "#FFFFFF", borderRadius: "22px", padding: "28px 24px", height: "100%", position: "relative" }}>
                      <Blob width={110} opacity={0.3} style={{ bottom: "-18px", right: "-20px" }} />
                      <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: c.tone, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <Icon size={22} color="#FFFFFF" strokeWidth={1.9} />
                      </div>
                      <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: "18px", color: INK, marginBottom: "8px" }}>
                        {c.title}
                      </h3>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px", color: MUTED, lineHeight: 1.65 }}>
                        {c.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHY */}
        <section style={{ background: CREAM, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: ORANGE_DEEP, letterSpacing: "0.06em" }}>
                  WHY RESELL KINTERON
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 44px)", color: INK, marginTop: "12px" }}>
                  A product that <HighlightWord bg={ORANGE}>sells itself</HighlightWord>
                </h2>
                <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: MUTED, maxWidth: "520px", margin: "16px auto 0", lineHeight: 1.7 }}>
                  JAMB &amp; WAEC season comes every year. Parents already pay for lesson
                  centres — an offline CBT program is an easy yes.
                </p>
              </div>
            </Reveal>
            <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "22px" }}>
              {[
                { icon: BadgePercent, title: "25–50% profit margin", body: "Buy from ₦1,000–₦1,500, sell at ₦2,000. Sell 10 copies at bulk rate and keep ₦7,000+.", tone: ORANGE },
                { icon: WifiOff, title: "Offline = easy to sell", body: "No internet needed after install. Perfect pitch for homes, villages and centres with poor network.", tone: INDIGO },
                { icon: ShieldCheck, title: "Real exam simulation", body: "Official JAMB keys, calculator, timer + AI tutor on every question. Demo it once and parents buy.", tone: FOREST },
                { icon: Truck, title: "Instant delivery", body: "Codes sent straight to your WhatsApp as KINT-XXXX-XXXX-XXXX. No waybill, no waiting, no stock to hold.", tone: ORANGE_DEEP },
                { icon: Headset, title: "Dedicated support", body: "Stuck install? Code not activating? Message us and we sort it with you and your customer.", tone: INDIGO_DEEP },
                { icon: Banknote, title: "Centres love it", body: "One code per machine — tutorial centres buy 10, 20, 50 at once to fill a whole computer lab.", tone: FOREST },
              ].map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.title} delay={i * 70}>
                    <div className="reseller-card" style={{ background: "#FFFFFF", borderRadius: "22px", padding: "28px 24px", height: "100%", position: "relative" }}>
                      <Blob width={110} opacity={0.3} style={{ bottom: "-18px", right: "-20px" }} />
                      <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: c.tone, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                        <Icon size={22} color="#FFFFFF" strokeWidth={1.9} />
                      </div>
                      <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: "18px", color: INK, marginBottom: "8px" }}>
                        {c.title}
                      </h3>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px", color: MUTED, lineHeight: 1.65 }}>
                        {c.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section style={{ background: MINT, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "48px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#33512F", letterSpacing: "0.06em" }}>
                  HOW RESELLING WORKS
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 44px)", color: INK, marginTop: "12px" }}>
                  Three steps to your first <HighlightWord bg={INDIGO} rotate="-1deg">payout</HighlightWord>
                </h2>
              </div>
            </Reveal>
            <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "22px" }}>
              {[
                { icon: UserPlus, num: "1.", title: "Say hello on WhatsApp", body: "Tell us your name, location and how many codes you want to start with. No form, no wahala — 2 minutes." },
                { icon: KeyRound, num: "2.", title: "Buy codes at reseller rate", body: "Pay, get KINT-XXXX activation codes instantly on WhatsApp. From ₦1,500 each — just ₦1,000 in bulk (50+)." },
                { icon: Banknote, num: "3.", title: "Sell & keep the profit", body: "Sell each copy at ₦2,000 to students, parents or centres. Every sale puts ₦500–₦1,000 straight in your pocket." },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.num} delay={i * 100}>
                    <div style={{ background: "#FFFFFF", borderRadius: "22px", padding: "30px 26px", height: "100%", position: "relative", textAlign: "center" }}>
                      <div style={{ width: "56px", height: "56px", borderRadius: "18px", background: INK, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                        <Icon size={24} color={CREAM} strokeWidth={1.8} />
                      </div>
                      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "13px", fontWeight: 700, color: ORANGE_DEEP, marginBottom: "6px" }}>
                        STEP {s.num}
                      </div>
                      <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "19px", color: INK, marginBottom: "10px" }}>
                        {s.title}
                      </h3>
                      <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13.5px", color: MUTED, lineHeight: 1.65 }}>
                        {s.body}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={150}>
              <div style={{ display: "flex", justifyContent: "center", marginTop: "36px", gap: "12px", flexWrap: "wrap" }}>
                <a
                  href={waLink("Hello Kinteron, I'd like to become a reseller. My name is ___, I am in ___, I want to start with ___ codes.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-whatsapp"
                >
                  <MessageCircle size={16} />
                  Start on WhatsApp now
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ background: CREAM, padding: "90px 40px" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: "44px" }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: ORANGE_DEEP, letterSpacing: "0.06em" }}>
                  RESELLER QUESTIONS
                </span>
                <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 3.8vw, 42px)", color: INK, marginTop: "12px" }}>
                  Before you <HighlightWord bg={FOREST} color={CREAM} rotate="1deg">ask</HighlightWord>
                </h2>
              </div>
            </Reveal>
            <div className="faq-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "18px" }}>
              {[
                { q: "How much do I buy each copy?", a: "Students pay ₦2,000 retail per code (JAMB or WAEC Desktop). Resellers buy from ₦1,500 (1–4 codes), ₦1,300 for 5+, ₦1,100 for 20+, and just ₦1,000 for 50+ bulk. You keep the difference." },
                { q: "Is there a minimum order?", a: "No. Start with even 1 code to test with your own computer or first customer. Bulk discounts kick in automatically as you scale." },
                { q: "How do I receive the codes?", a: "Instantly on WhatsApp after payment — each code looks like KINT-XXXX-XXXX-XXXX. Forward it to your customer or print it. One code activates one computer, forever." },
                { q: "What if a code doesn't work?", a: "Message us with the code and the error on screen. We replace genuine failed activations and help with installation — including TeamViewer-style guidance for centres." },
                { q: "Can I run a CBT centre with this?", a: "Yes — that's the best use. Buy one code per machine (e.g. 20 codes for 20 computers). Centres charge students per session while the software is a one-time cost." },
                { q: "Do I need to be in Lagos?", a: "No. Everything happens on WhatsApp and the software works fully offline after install, so resellers anywhere in Nigeria can sell in their town, school or church." },
              ].map((f, i) => (
                <Reveal key={f.q} delay={i * 60}>
                  <div className="reseller-card" style={{ background: "#FFFFFF", borderRadius: "20px", padding: "24px", height: "100%" }}>
                    <h3 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 600, fontSize: "16.5px", color: INK, marginBottom: "10px", lineHeight: 1.35 }}>
                      {f.q}
                    </h3>
                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px", color: MUTED, lineHeight: 1.65 }}>
                      {f.a}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section style={{ padding: "40px 40px 80px" }}>
          <div
            style={{
              maxWidth: "1000px",
              margin: "0 auto",
              background: INK,
              borderRadius: "26px",
              padding: "56px 48px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <StarBurst size={24} color={ORANGE} className="twinkle-slow" style={{ top: "30px", left: "6%" }} />
            <StarBurst size={18} color={ORANGE} className="twinkle-fast" style={{ bottom: "36px", right: "8%" }} />
            <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "clamp(26px, 3.6vw, 40px)", color: CREAM, lineHeight: 1.2, marginBottom: "14px" }}>
              Exam season is profit season.
              <br />
              Start earning today.
            </h2>
            <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: "rgba(253,248,240,0.75)", maxWidth: "480px", margin: "0 auto 30px", lineHeight: 1.65 }}>
              Message us now — tell us your name and town, and we'll set you up with
              your first codes in minutes.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a
                href={waLink("Hello Kinteron, I want to JOIN AS A RESELLER now. My name is ___, I'm in ___.")}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-whatsapp"
              >
                <MessageCircle size={16} />
                Join as Reseller Now
              </a>
              <button className="cta-line" onClick={scrollToCalc}>
                <Calculator size={16} />
                Recalculate profit
              </button>
            </div>
            <div style={{ marginTop: "26px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: "rgba(253,248,240,0.6)" }}>
              <CheckCircle2 size={14} color="#25D366" />
              No signup fee · No stock to hold · Codes delivered same day
            </div>
          </div>
        </section>

        {/* CONTACT STRIP */}
        <section style={{ padding: "0 40px 80px" }}>
          <div style={{ maxWidth: "1000px", margin: "0 auto", display: "flex", gap: "14px", flexWrap: "wrap", justifyContent: "center" }}>
            <a href={waLink("Hello Kinteron, I have a question about reselling.")} target="_blank" rel="noopener noreferrer" className="cta-line" style={{ background: "#E7F9EE", borderColor: MINT_DEEP }}>
              <MessageCircle size={16} color={FOREST} />
              WhatsApp: 0704 425 7292
            </a>
            <a href="tel:+2347044257292" className="cta-line">
              <Zap size={16} />
              Call: 0704 425 7292
            </a>
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ borderTop: `1px solid ${LINE}`, background: CREAM }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto", padding: "36px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "14px" }}>
            <a href="/" style={{ fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, fontSize: "18px", color: INK, textDecoration: "none" }}>
              Kinteron
            </a>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: MUTED, textAlign: "center" }}>
              Kinteron is not affiliated with JAMB, WAEC, NECO, or any government examination body.
            </span>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "12.5px", color: MUTED }}>
              © 2026 Kinteron. Made in Nigeria 🇳🇬
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}
